import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Check, ChevronsUpDown, Loader2, Star } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { reachesCompany, worksAtAssignedVenues } from '@/auth/access'
import { isInVenueSupportMode } from '@/auth/roles'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useEnterVenue } from '@/hooks/useEnterVenue'
import { useSetPrimaryVenue, useSwitchableVenues } from '@/hooks/useSwitchableVenues'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip } from '@/components/ui/tooltip'
import { FormError } from '@/components/ui/form-error'
import { cn } from '@/lib/utils'

/**
 * Sidebar control naming the venue you are working in, with the venues you can
 * move to.
 *
 * It serves two audiences, and the difference is not cosmetic:
 *
 * - **Staff on assigned venues** always have a venue — it is their home. The
 *   list is the venues assigned to them, and starring one sets where they land
 *   at sign-in.
 * - **Staff whose role reaches the company** — an organization admin, a
 *   Company-wide role, or a super admin inside a tenant — only see this while
 *   they have entered a venue. Their list is every venue in the organization,
 *   and the first entry leaves for the organization again. There is no
 *   starring: their home is the organization, not a venue.
 *
 * This is also the only way out of a venue for an organization admin, which is
 * why the exit is the first thing in the list rather than buried under it.
 */
export function VenueSwitcher() {
  const { user, exitVenue } = useAuth()
  const navigate = useNavigate()
  const venuesQuery = useSwitchableVenues()
  const { enter, enteringId, isEntering, error: enterError } = useEnterVenue()
  const setPrimary = useSetPrimaryVenue()
  const [open, setOpen] = React.useState(false)
  const [exiting, setExiting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const venueAdmin = worksAtAssignedVenues(user)
  const inVenue = isInVenueSupportMode(user)
  // Someone working across the company has no venue to name, and a permanent
  // picker would imply one is always selected.
  const orgAdminInVenue = reachesCompany(user) && inVenue

  if (!venueAdmin && !orgAdminInVenue) {
    return null
  }

  const venues = venuesQuery.data?.venues ?? []
  const currentId = inVenue && user ? user.supportVenue.venueId : null
  const currentName = inVenue && user ? user.supportVenue.venueName : null

  // Assigned to nothing: say so rather than showing an empty menu. Only staff on
  // assigned venues can be in this position; the company's see every venue.
  if (venueAdmin && !venuesQuery.isPending && venues.length === 0) {
    return (
      <div className="mx-3 mb-3.5 rounded-md border border-dashed border-sidebar-ink-muted px-2.5 py-2 text-xs text-sidebar-ink-muted">
        No venue assigned yet. Ask an organization admin to add you to one.
      </div>
    )
  }

  // For staff on assigned venues a single venue is not a choice — name it, no
  // menu. Someone from across the company still needs the menu, because it
  // holds their way out.
  if (venueAdmin && venues.length === 1) {
    return (
      <div className="mx-3 mb-3.5 grid gap-0.5 rounded-md border border-sidebar-line bg-sidebar-hover px-2.5 py-2">
        <span className="font-eyebrow leading-[14px]">Venue</span>
        <span className="truncate text-control font-semibold text-sidebar-ink">
          {currentName ?? venues[0]?.name}
        </span>
      </div>
    )
  }

  async function handleSwitch(venueId: string): Promise<void> {
    if (venueId === currentId) {
      setOpen(false)
      return
    }
    setError(null)
    // Only dismiss on success. Since the spinner is cleared either way, closing
    // regardless would make a venue that failed to open look like one that did.
    if (await enter(venueId)) {
      setOpen(false)
    }
  }

  async function handleBackToOrganization(): Promise<void> {
    setError(null)
    setExiting(true)
    try {
      await exitVenue()
      setOpen(false)
      navigate('/venues/all', { replace: true })
    } catch (e) {
      setError(requestErrorMessage(e))
    } finally {
      setExiting(false)
    }
  }

  async function handleMakeDefault(venueId: string): Promise<void> {
    setError(null)
    try {
      await setPrimary.mutateAsync(venueId)
    } catch (e) {
      setError(requestErrorMessage(e))
    }
  }

  return (
    <div className="mx-3 mb-3.5">
      <Popover open={open} onOpenChange={setOpen}>
        {/* Plan View's scope card: where you are, as a raised panel on the
            blackwood sidebar — a mono label over the name — and the way to go
            elsewhere. */}
        <PopoverTrigger
          className={cn(
            'flex w-full items-center gap-2 rounded-md border border-sidebar-line bg-sidebar-hover px-2.5 py-2 text-left outline-none',
            'transition-colors hover:bg-sidebar-active focus-visible:focus-ds',
          )}
        >
          <span className="grid min-w-0 flex-1 gap-0.5">
            <span className="font-eyebrow leading-[14px]">{currentName ? 'Venue' : 'Organization'}</span>
            <span
              className={cn(
                'truncate text-control font-semibold',
                currentName ? 'text-sidebar-ink' : 'text-sidebar-ink-muted',
              )}
            >
              {currentName ?? 'Choose a venue'}
            </span>
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-sidebar-ink-muted" aria-hidden />
        </PopoverTrigger>
        <PopoverContent className="w-[16rem] p-1">
          {orgAdminInVenue ? (
            <div className="mb-1 border-b border-line pb-1">
              <button
                type="button"
                disabled={exiting}
                onClick={() => void handleBackToOrganization()}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-surface-hover disabled:opacity-60"
              >
                {exiting ? (
                  <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
                ) : (
                  <ArrowLeft className="h-3.5 w-3.5 shrink-0" aria-hidden />
                )}
                <span className="truncate">
                  {exiting ? 'Leaving…' : 'Back to organization'}
                </span>
              </button>
            </div>
          ) : null}
          <ul className="max-h-72 overflow-y-auto">
            {venues.map((venue) => {
              const isCurrent = venue.id === currentId
              const busy = enteringId === venue.id
              return (
                <li key={venue.id} className="flex items-center gap-1">
                  <button
                    type="button"
                    disabled={isEntering}
                    onClick={() => void handleSwitch(venue.id)}
                    className="flex min-w-0 flex-1 items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-surface-hover disabled:opacity-60"
                  >
                    {busy ? (
                      <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
                    ) : (
                      <Check
                        className={cn('h-3.5 w-3.5 shrink-0', isCurrent ? 'opacity-100' : 'opacity-0')}
                        aria-hidden
                      />
                    )}
                    <span className="truncate">{venue.name}</span>
                  </button>
                  {/* Starring picks where you land at sign-in, which only means
                      something for someone whose home is a venue. */}
                  {venueAdmin ? (
                    <Tooltip content={venue.isPrimary ? 'Your default venue' : 'Make this my default venue'}>
                      <button
                        type="button"
                        aria-label={venue.isPrimary ? 'Your default venue' : 'Make this my default venue'}
                        aria-pressed={venue.isPrimary}
                        disabled={venue.isPrimary || setPrimary.isPending}
                        onClick={() => void handleMakeDefault(venue.id)}
                        className="rounded-sm p-1.5 text-ink-muted hover:bg-surface-hover hover:text-ink disabled:hover:bg-transparent"
                      >
                        <Star className={cn('h-3.5 w-3.5', venue.isPrimary && 'fill-current text-ink')} />
                      </button>
                    </Tooltip>
                  ) : null}
                </li>
              )
            })}
          </ul>
          {error ?? enterError ? (
            <FormError size="sm" className="px-2 pb-1 pt-2">{error ?? enterError}</FormError>
          ) : null}
          <p className="border-t border-line px-2 pb-1 pt-2 text-xs text-ink-muted">
            {venueAdmin
              ? 'You work in one venue at a time. The starred venue is where you start.'
              : 'You are working inside one venue. Leave to manage the whole organization.'}
          </p>
        </PopoverContent>
      </Popover>
    </div>
  )
}
